import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TOUCH_TARGET } from '../components/touch-target';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  FolderArrowDownIcon,
  FolderPlusIcon,
  FunnelIcon,
  PencilSquareIcon,
  PhotoIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { FolderOpResult, Rating, SavedFilter, SavedFilterIcon } from '@xphoto/shared';
import { useI18n, type MessageKey } from '../i18n';
import { useAuth, useCanWrite } from '../auth';
import { clearAppContext, setAppContext } from '../diagnostics/context';
import { recordAction } from '../diagnostics/buffers';
import { interpolate } from './format';
import { LIBRARY_ID, DENSITIES, type Density } from './config';
import {
  GALLERY_TABS,
  applySavedFilter,
  galleryStateToExprText,
  hasAdvancedFilter,
  hasExifFilter,
  hasExprFilter,
  stateToFilters,
  type GalleryState,
} from './state';
import { folderLinkFor } from './url-state';
import { minimalGalleryLink, resolveGalleryHref, sortScopeFor } from './sort-resolution';
import { broadcastGalleryRefresh, subscribeGalleryRefresh } from './gallery-refresh';
import { accountRandomSeed } from './random-seed';
import { defaultDirForSort, getSortPrefs, storeSortPref } from './default-sort-pref';
import { getDensity, storeDensity } from './density-pref';
import { GalleryViewerContext, type GalleryViewerContextValue } from './viewer-context';
import { openViewerSlideshow, openViewerTagging } from '../viewer';
import { Toolbar } from './Toolbar';
import { ToolsMenu } from './ToolsMenu';
import { Grid } from './Grid';
import { FacetPanel, type FacetItem } from './FacetPanel';
import { FolderGrid } from './FolderGrid';
import { folderPathSegments } from './folder-path';
import { useAssets, type AssetsErrorKind } from './use-assets';
import { useFolderTree, useTags, useCounts } from './use-facets';
import { useJobActivity } from '../notifications/use-job-activity';
import { hasActiveJobOfType } from '../notifications/job-activity';
import { useRefreshAfterJob } from '../notifications/use-refresh-after-job';
import { Portal } from '../components/Portal';
import { MoveToFolderModal, type MoveTarget } from '../organize/MoveToFolderModal';
import { UploadModal } from '../organize/UploadModal';
import { FolderOpModal, type FolderOpKind, type FolderOpTarget } from '../organize/FolderOpModal';
import { invalidateAllFolders } from '../organize/use-all-folders';
import { FixExtensionsModal } from '../organize/FixExtensionsModal';
import { CleanEmptyFoldersModal } from '../organize/CleanEmptyFoldersModal';
import { ConfirmModal } from '../taxonomy/ConfirmModal';
import { BulkRatingModal } from './BulkRatingModal';
import { BulkTagModal, type BulkTagMode } from './BulkTagModal';
import { CompressModal } from './CompressModal';
import { RotateModal } from './RotateModal';
import { AdvancedSearchModal, type AdvancedCriteria } from './AdvancedSearchModal';
import { AdvancedFilterChips } from './AdvancedFilterChips';
import { ExprFilterChips } from './ExprFilterChips';
import { FilterExpressionModal } from './FilterExpressionModal';
import { SaveFilterDialog } from './SaveFilterDialog';
import { ManageSavedFiltersModal } from './ManageSavedFiltersModal';
import { createSavedFilter, fetchSavedFilters } from './saved-filters-api';
import {
  acceptAiTagsForSelection,
  addTagsToSelection,
  downloadSelectionZip,
  removeTagsFromSelection,
  setRatingForSelection,
} from './bulk-api';
import { fetchAllAssetIds } from './api';
import { softDeleteAssets } from '../trash/trash-api';
import { ApiError } from './api';
import { saveScrollStash, useScrollRestore } from '../lib/scroll-restore';
import { enqueueAiTag } from '../ai/ai-tag-api';
import { AiApiError } from '../ai/ai-api';
import { useAppConfig } from '../app-config';

/**
 * Préfixe de clé de réserve du défilement des cartes de sous-dossiers. Suffixé par le
 * CHEMIN du dossier courant (`folderGrid:<path>`) → chaque niveau restaure sa propre
 * position quand on descend puis remonte dans l'arborescence.
 */
const FOLDER_GRID_SCROLL_KEY = 'folderGrid';

/**
 * Écran Galerie (T2b) — désormais PILOTÉ PAR L'URL (« tout dans l'URL »).
 *
 * L'onglet vient du CHEMIN (`/`, `/dossiers`, `/tags`) et le reste de
 * l'état de requête (tri, sens, recherche, dossier/tag, filtres EXIF) de la
 * QUERY-STRING — restaurés au chargement (deep-link) et mis à jour par navigation.
 *
 * `GalleryView` est un LAYOUT des routes galerie + visionneuse : il rend le corps
 * de la galerie DANS la zone `main` de la coquille partagée (`AppChrome`, qui
 * fournit l'en-tête persistant), et laisse la VISIONNEUSE (`/photo/:id`) s'ouvrir
 * en surcouche via son `<Outlet>`. Paramètres / Admin / Outil EXIF ne sont PLUS des
 * enfants d'ici : ce sont des PAGES sœurs sous `AppChrome` (voir l'arbre de routes
 * dans `App`), rendues sous le même en-tête.
 *
 * La galerie reste montée sur ces routes → ses données ne se démontent pas quand la
 * visionneuse s'ouvre, qui peut donc naviguer préc./suiv. dans la liste chargée (via
 * `GalleryViewerContext`). La densité S/M/L reste une préférence d'UI LOCALE (pas
 * dans l'URL : ce n'est pas un « emplacement » adressable).
 */
export function GalleryView(): React.JSX.Element {
  const { t, tp, locale } = useI18n();
  const { user, reshuffleRandomSeed } = useAuth();
  const { aiEnabled } = useAppConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const libraryId = user?.libraryId ?? LIBRARY_ID;

  // Graine de l'ordre aléatoire PAR UTILISATEUR (persistée côté serveur → même ordre,
  // stable, sur toutes les machines/sessions du compte), avec repli DÉFENSIF PARTAGÉ si la
  // graine serveur manque (contrat `randomSeed` optionnel). Elle n'est plus dans l'URL
  // (LOT C1) : elle est injectée dans l'état par `resolveGalleryState`, et « Re-mélanger »
  // la change ICI (nouvelle valeur du provider) → la grille se recharge sans navigation.
  const accountSeed = accountRandomSeed(user?.randomSeed);

  // Densité S/M/L : préférence d'UI LOCALE, persistée en `localStorage` (audit C2) —
  // comme le tri par défaut et les options de visionneuse. Hors URL à dessein (ce n'est
  // pas un « emplacement » adressable).
  const [density, setDensityState] = useState<Density>(getDensity);
  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
    storeDensity(next);
  }, []);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /**
   * MODE SÉLECTION EXPLICITE (v1.41.0) — armé par le bouton « Sélectionner » de la barre
   * d'outils. Il REMPLACE le mode implicite de la v1.35.0, qui rendait la case de chaque
   * tuile visible en permanence au tactile : sur un vrai téléphone, une case blanche sur
   * chaque vignette rendait la galerie illisible (retour d'usage).
   *
   * L'état vit ICI parce que c'est le seul endroit qui connaît À LA FOIS la sélection et
   * la barre d'outils. Il est volontairement SÉPARÉ de `selected` : la règle effective
   * est `selectionMode = explicite || sélection non vide` (voir plus bas), ce qui unifie
   * les deux mondes sans jamais brancher sur le type de pointeur — au tactile on entre
   * par le bouton, au survol (desktop) on entre en cochant une case.
   */
  const [explicitSelectionMode, setExplicitSelectionMode] = useState(false);
  // « Tout sélectionner » : récupère TOUS les ids du groupe filtré (pas seulement les
  // tuiles chargées). `busy` désactive le bouton pendant l'appel ; `error` affiche un
  // message non bloquant (jamais d'alerte native).
  const [selectAllBusy, setSelectAllBusy] = useState(false);
  const [selectAllError, setSelectAllError] = useState<string | undefined>(undefined);
  // Téléchargement de la sélection en ZIP : `busy` désactive le bouton pendant la
  // préparation (potentiellement longue) ; `error` affiche un message non bloquant.
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);
  // « Réviser ces N photos » (vue « À valider (IA) ») : récupère TOUS les ids du groupe
  // filtré avant d'ouvrir le tagueur. `busy` affiche « Préparation… » ; `error` (ensemble
  // vide ou échec réseau) est affiché en alerte inline non bloquante.
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState<string | undefined>(undefined);
  // --- Clés d'invalidation CIBLÉES (audit A4) ---------------------------------------
  //
  // Il y avait UNE clé globale consommée par `useFolders` + `useTags` + `useCounts` : la
  // bumper après une NOTATION DE MASSE relançait `/folders` + `/tags` + `/counts` alors
  // qu'une note ne change NI l'arborescence, NI la taxonomie, NI les compteurs — 1 POST
  // utile pour 3 GET inutiles. Le code se contredisait d'ailleurs lui-même : la note
  // posée depuis la visionneuse documentait « pas de rafraîchissement des compteurs ».
  //
  // Une facette PÉRIMÉE est un bug VISIBLE, sous-invalider serait donc pire que
  // sur-invalider. Table de ce que chaque action peut RÉELLEMENT changer :
  //
  //   action                                   | dossiers | tags | compteurs | grille
  //   -----------------------------------------|----------|------|-----------|-------
  //   fin de scan / rafraîchissement externe    |    ✔     |  ✔   |     ✔     |   ✔
  //   corbeille (soft-delete)                   |    ✔     |  ✔   |     ✔     |   ✔
  //   téléversement                             |    ✔     |  –   |     ✔     | (scan)
  //   déplacement enfilé                        |    ✔     |  –   |     –     |   –
  //   correction d'extensions enfilée           |    ✔     |  –   |     –     |   –
  //   compression / rotation enfilée            |    –     |  –   |     –     |   –
  //   tags en masse (ajouter / retirer / IA)    |    –     |  ✔   |     –     |   –
  //   notation de masse                         |    –     |  –   |     –     |   –
  //
  // Justifications des lignes non évidentes :
  //  - déplacement / extensions : les CHEMINS changent (arborescence), mais ni la
  //    taxonomie ni aucun compteur (mêmes assets, mêmes types, même « à valider »).
  //  - compression / rotation : mêmes fichiers, mêmes dossiers, mêmes tags, mêmes
  //    compteurs — seuls les octets/l'orientation changent.
  //  - « Accepter les suggestions IA » promeut `proposed`→`validated` : les USAGES de tags
  //    bougent (facette Tags), mais le code note explicitement que `tagged` ne change pas,
  //    donc le compteur « à valider (IA) » non plus.
  //  - notation de masse : la note n'apparaît dans AUCUNE facette ni AUCUN compteur ; les
  //    tuiles chargées sont déjà mises à jour localement par `patchAsset`.
  const [folderRefreshKey, setFolderRefreshKey] = useState(0);
  const [tagRefreshKey, setTagRefreshKey] = useState(0);
  const [countsRefreshKey, setCountsRefreshKey] = useState(0);

  // L'arborescence a changé : facette « Dossiers » + arbre partagé des modales (A9).
  const bumpFolders = useCallback(() => {
    setFolderRefreshKey((k) => k + 1);
    invalidateAllFolders();
  }, []);
  const bumpTags = useCallback(() => setTagRefreshKey((k) => k + 1), []);
  const bumpCounts = useCallback(() => setCountsRefreshKey((k) => k + 1), []);
  /** Source de vérité inconnue (scan, corbeille) → tout est potentiellement périmé. */
  const bumpAllFacets = useCallback(() => {
    bumpFolders();
    bumpTags();
    bumpCounts();
  }, [bumpFolders, bumpTags, bumpCounts]);
  // Cible du déplacement en cours (modale ouverte si non nulle) : vidéos avec son
  // ou sélection courante (LOT 2).
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  // Compression SANS PERTE (ids CAPTURÉS à l'ouverture de la modale). Modale ouverte si non nul.
  const [compressTarget, setCompressTarget] = useState<string[] | null>(null);
  // Rotation « Pivoter » (ids CAPTURÉS à l'ouverture de la modale). Modale ouverte si non nul.
  const [rotateTarget, setRotateTarget] = useState<string[] | null>(null);
  // Téléversement (XPHOTO-16) : modale ouverte si vrai.
  const [uploadOpen, setUploadOpen] = useState(false);
  const [fixExtensionsOpen, setFixExtensionsOpen] = useState(false);
  // Nettoyage des dossiers vides (répertoires disque sans aucun fichier) : modale ouverte si vrai.
  const [cleanEmptyFoldersOpen, setCleanEmptyFoldersOpen] = useState(false);
  // Recherche avancée (note min / types / tags) : panneau ouvert si vrai.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Filtres enregistrés + expression booléenne (menu « Mes filtres ») :
  //  - `savedFilters` : la liste (triée par position), chargée par bibliothèque ;
  //  - `exprEditorOpen` : éditeur d'expression avancée ouvert ;
  //  - `saveExprTarget` : expression en cours d'enregistrement (dialogue nom+icône ouvert) ;
  //  - `manageOpen` : écran « Gérer mes filtres » ouvert.
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [savedFiltersError, setSavedFiltersError] = useState<string | undefined>(undefined);
  const [exprEditorOpen, setExprEditorOpen] = useState(false);
  const [saveExprTarget, setSaveExprTarget] = useState<string | null>(null);
  const [saveExprBusy, setSaveExprBusy] = useState(false);
  const [saveExprError, setSaveExprError] = useState<string | undefined>(undefined);
  const [manageOpen, setManageOpen] = useState(false);
  // Corbeille (Lot 1) : confirmation de « mise à la poubelle » de la sélection ouverte.
  // On CAPTURE les ids au moment de l'ouverture (la sélection peut changer avant confirmation).
  const [deleteTarget, setDeleteTarget] = useState<string[] | null>(null);
  // Appel réseau de suppression en cours (désactive la confirmation) + message d'erreur.
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);
  // Notation de masse (LOT 3) : ids CAPTURÉS à l'ouverture de la modale (la sélection
  // peut changer avant « Appliquer »), + appel réseau en cours + message d'erreur.
  const [rateTarget, setRateTarget] = useState<string[] | null>(null);
  const [rateBusy, setRateBusy] = useState(false);
  const [rateError, setRateError] = useState<string | undefined>(undefined);
  // Ajout de tags EXISTANTS en masse (LOT 4) : ids CAPTURÉS à l'ouverture de la modale
  // (la sélection peut changer avant « Appliquer »), + appel réseau en cours + erreur.
  const [tagTarget, setTagTarget] = useState<string[] | null>(null);
  const [tagBusy, setTagBusy] = useState(false);
  const [tagError, setTagError] = useState<string | undefined>(undefined);
  // Acceptation en masse des suggestions IA (« Accepter les suggestions IA », Lot 2) :
  // ids CAPTURÉS à l'ouverture de la confirmation + appel réseau en cours + erreur.
  const [acceptAiTarget, setAcceptAiTarget] = useState<string[] | null>(null);
  const [acceptAiBusy, setAcceptAiBusy] = useState(false);
  const [acceptAiError, setAcceptAiError] = useState<string | undefined>(undefined);
  // Tagging IA (T5 lot C) : confirmation via NOTRE modale (jamais de `window.confirm`).
  // On capture les ids ciblés + le message à l'ouverture ; l'enfil se fait à la confirmation.
  const [aiTagConfirm, setAiTagConfirm] = useState<{ ids: string[]; message: string } | null>(null);
  const [aiTagBusy, setAiTagBusy] = useState(false);
  // Résolveur de la Promesse rendue à la Toolbar (`onTagAi`) : résolu par la modale
  // (confirmer → enfil → outcome ; annuler → `'ok'`), pour garder le contrat inchangé.
  const aiTagResolveRef = useRef<
    ((outcome: 'ok' | 'no-provider' | 'disabled' | 'error') => void) | null
  >(null);

  // Overlay ouvert (viewer/exif/…) = présence d'une route enfant.
  const viewerMatch = /^\/photo\/([^/]+)$/.exec(location.pathname);
  const viewerOpen = viewerMatch !== null;
  const viewerAssetId = viewerMatch?.[1];

  // URL de GALERIE EN VIGUEUR — la barre d'adresse quand la grille est au premier plan,
  // l'URL de retour (`state.back`) quand la visionneuse la recouvre.
  //
  // POURQUOI PAS le chemin courant dans ce second cas : `GalleryView` est un LAYOUT
  // PERSISTANT ; l'URL devient alors `/photo/:id`, qui ne porte NI l'onglet
  // (`pathToTab('/photo/…')` retombe sur « gallery ») NI `?dossier`/`?tag`/tri. La grille
  // s'effondrerait sur la bibliothèque ENTIÈRE, et son contexte partagé (`getAsset`/
  // `indexOfId`, lu par la route visionneuse) refléterait le MAUVAIS ensemble — la
  // visionneuse feuilletterait toute la biblio au lieu du dossier ouvert (bug v0.53.0).
  //
  // À FROID (lien collé, F5, lien partagé) il n'y a AUCUN `back` : on retombe sur `/`, le
  // MÊME repli que `ViewerRoute` (`routeState?.back ?? '/'`).
  const backHref = (location.state as { back?: string } | null)?.back;
  const galleryHref = viewerOpen
    ? typeof backHref === 'string'
      ? backHref
      : '/'
    : `${location.pathname}${location.search}`;

  // État de requête DÉRIVÉ de cette URL par le résolveur PARTAGÉ avec `ViewerRoute` — c'est
  // lui qui applique la préférence de tri et injecte la graine du COMPTE (`sort-resolution.ts`).
  //
  // AUCUNE RÉÉCRITURE D'URL (LOT C1) : la mécanique `appliedPreference` →
  // `navigate(canonicalHref, { replace: true })` a été retirée, donc l'Accueil reste `/`,
  // tout court. Elle n'existait que pour rendre le tri effectif LISIBLE dans l'URL, dont
  // dépendait la cohérence de `back` ; les deux côtés résolvant désormais le tri à
  // l'identique, un `back` nu décrit le même ensemble ordonné que la grille. Bénéfice
  // collatéral : plus aucun risque de démonter la visionneuse en remplaçant `/photo/:id`
  // par un chemin de galerie (bug du deep-link à froid).
  //
  // `galleryHref` est aussi le `back` mémorisé à l'ouverture d'une photo, TEL QUEL (jamais
  // canonisé par `galleryLinkFor`) : l'Accueil reste `/` après un aller-retour dans la
  // visionneuse, et une URL LEGACY `?graine=XYZ` garde SA graine des deux côtés (canonisée,
  // elle aurait été effacée côté visionneuse — qui serait repartie sur la graine du compte,
  // donc sur un ORDRE DIFFÉRENT de la grille).
  const state = useMemo<GalleryState>(
    () => resolveGalleryHref(galleryHref, { ...getSortPrefs(), accountSeed }),
    [galleryHref, accountSeed],
  );

  // Emplacement de préférence du contexte COURANT (accueil vs navigation). Dérivé du
  // chemin de la GALERIE EN VIGUEUR, pas de `location.pathname` : celui-ci vaut
  // `/photo/:id` quand la visionneuse recouvre la grille, ce qui rangerait un dossier
  // ouvert dans l'emplacement « accueil ».
  const sortScope = sortScopeFor(galleryHref.split('?')[0] ?? '/');

  const exifToolOpen = location.pathname === '/outils/exif';
  const adminOpen = location.pathname.startsWith('/admin');

  // Publie le contexte d'écran (métadonnées) pour l'enveloppe « Signaler un bug ».
  useEffect(() => {
    setAppContext({
      libraryId: libraryId ?? null,
      tab: state.tab,
      viewerOpen,
      ...(viewerAssetId ? { viewerAssetId } : {}),
      exifToolOpen,
      adminOpen,
      density,
      selectedCount: selected.size,
    });
  }, [
    libraryId,
    state.tab,
    viewerOpen,
    viewerAssetId,
    exifToolOpen,
    adminOpen,
    density,
    selected.size,
  ]);
  useEffect(() => () => clearAppContext(), []);

  const filters = useMemo(() => stateToFilters(state), [state]);
  const assets = useAssets(libraryId, filters);

  // Navigation façon EXPLORATEUR de fichiers (onglet Dossiers) : à chaque niveau on
  // affiche les CARTES de sous-dossiers directs + les photos DIRECTEMENT dans le dossier
  // courant. Un lien profond EXIF (champ/publication) reste RÉCURSIF (rendu comme une
  // galerie filtrée avec sa puce, pas comme l'explorateur) → il n'entre PAS dans ce mode.
  const inFolderExplorer = state.tab === 'folders' && !hasExifFilter(state);
  // Dossier courant (racine = ''). Sert de `parent` aux facettes de sous-dossiers directs.
  const folderPath = state.folder ?? '';
  // Facettes chargées UNIQUEMENT quand elles sont affichées (audit A3) : `folders` n'est
  // consommé que par l'explorateur de dossiers, `tags` que par le panneau de l'onglet
  // « Tags ». Sur l'onglet Galerie, les deux réponses étaient jetées.
  //
  // XPHOTO-32 : l'arbre vient désormais de `/folder-tree` (lu sur le DISQUE, `ADR-0013`)
  // et non des seules facettes SQL — c'est ce qui rend les répertoires VIDES visibles.
  // Les compteurs, eux, restent servis par SQL : la route les fusionne.
  const folders = useFolderTree(
    libraryId,
    folderRefreshKey,
    inFolderExplorer ? folderPath : '',
    inFolderExplorer,
  );
  const tags = useTags(libraryId, tagRefreshKey, state.tab === 'tags');
  // Compteurs (dont `withAudio`) pour le badge de la facette « Avec son ».
  const counts = useCounts(libraryId ?? '', countsRefreshKey);

  // Activité des jobs (partagée avec la cloche) → verrouille le scan mono-instance :
  // tant qu'un job `scan` est ACTIF pour la bibliothèque, l'entrée « Lancer un scan »
  // du menu Outils est grisée + libellée « Scan en cours… » (anti double-lancement).
  const jobActivity = useJobActivity(libraryId ?? '');
  const scanActive = hasActiveJobOfType(jobActivity, 'scan');

  // Auto-rafraîchissement APRÈS la fin d'un scan (`scan`). Enfiler un scan ne change RIEN
  // immédiatement (rien n'est encore indexé) : rafraîchir dès l'enfilage n'afficherait que
  // les ANCIENS chiffres. On ARME l'observation à l'enfilage (`onScanQueued` →
  // `markScanPending`) puis, à la FIN du job (front descendant, ou repli par délai s'il
  // s'est terminé trop vite pour être vu actif), on rafraîchit compteurs + facettes
  // (Tags/Dossiers) ET on recharge la grille pour faire apparaître les fichiers nouvellement
  // indexés — le tout sans F5.
  const onScanFinished = useCallback(() => {
    bumpAllFacets();
    assets.reload();
  }, [bumpAllFacets, assets]);
  const { markPending: markScanPending } = useRefreshAfterJob(
    libraryId ?? '',
    'scan',
    onScanFinished,
  );

  // Abonnement au signal EXTERNE de rafraîchissement (`gallery-refresh`) : quand la cloche
  // demande une actualisation — un scan de FOND (autre appareil/session) qui a réellement
  // changé la bibliothèque s'est terminé, sans avoir été armé localement ici — on rejoue le
  // MÊME rafraîchissement in-place qu'une fin de scan armée (compteurs + facettes + grille),
  // filtres préservés, sans navigation ni remontage. `subscribeGalleryRefresh` renvoie sa
  // fonction de désinscription, rendue directement au nettoyage de l'effet.
  useEffect(() => subscribeGalleryRefresh(onScanFinished), [onScanFinished]);

  // Navigue vers un nouvel état de galerie (chemin + query) ; remplace l'entrée
  // pour les changements de filtres (évite d'empiler l'historique à chaque frappe).
  //
  // Le lien est MINIMAL (`minimalGalleryLink`) : `tri`/`ordre` ne sont écrits que s'ils
  // DIVERGENT de la préférence du contexte d'arrivée — sinon l'accueil, propre à froid
  // (`/`), se serait sali en `/?tri=random&q=chat` à la première recherche. Les
  // préférences sont relues à CHAQUE appel (`getSortPrefs`), jamais capturées : voir
  // l'ordre volontaire « naviguer puis persister » dans `onSortChange`.
  const goToState = useCallback(
    (next: GalleryState, replace = true) => {
      navigate(minimalGalleryLink(next, { ...getSortPrefs(), accountSeed }), { replace });
    },
    [navigate, accountSeed],
  );

  // Effacer le filtre EXIF = REVENIR à l'outil d'origine (pas rester dans la galerie
  // principale). Le filtre provient toujours d'un outil : un champ EXIF (`hasExifField`)
  // vient du « Nettoyage EXIF », un état de publication (`publishState`) vient de
  // « Publier des tags vers EXIF ».
  const clearExifFilter = useCallback(() => {
    navigate(state.hasExifField ? '/outils/exif' : '/outils/exif-publier');
  }, [navigate, state.hasExifField]);

  // Recherche : « Appliquer » pousse TOUS les critères dans l'état (sérialisés dans
  // l'URL, donc propagés à la liste, à la visionneuse et au « Tout sélectionner ») —
  // dont le NOM DE FICHIER (`search` → `q`), désormais géré par le panneau.
  const applyAdvanced = useCallback(
    (criteria: AdvancedCriteria) => {
      goToState({
        ...state,
        search: criteria.filename,
        ratingMin: criteria.ratingMin,
        tagIds: criteria.tagIds,
        tagMode: criteria.tagMode,
      });
      setAdvancedOpen(false);
    },
    [goToState, state],
  );

  // Retrait d'un critère depuis sa puce (les autres critères restent).
  const clearAdvancedFilename = useCallback(
    () => goToState({ ...state, search: '' }),
    [goToState, state],
  );
  const clearAdvancedRating = useCallback(
    () => goToState({ ...state, ratingMin: null }),
    [goToState, state],
  );
  const clearAdvancedTags = useCallback(
    () => goToState({ ...state, tagIds: [], tagMode: 'any' }),
    [goToState, state],
  );

  // --- Filtres enregistrés + expression booléenne (« Mes filtres »). ---

  // Charge la liste des filtres enregistrés par bibliothèque (annulable au démontage).
  useEffect(() => {
    if (!libraryId) return;
    let cancelled = false;
    const ctrl = new AbortController();
    setSavedFiltersLoading(true);
    setSavedFiltersError(undefined);
    void (async () => {
      try {
        const list = await fetchSavedFilters(libraryId, ctrl.signal);
        if (!cancelled) setSavedFilters(list);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (!cancelled) setSavedFiltersError(t('savedFilters.loadError'));
      } finally {
        if (!cancelled) setSavedFiltersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [libraryId, t]);

  // Applique un filtre AVANCÉ (menu OU éditeur) : état PROPRE (`applySavedFilter`) + `expr`.
  const applyExpr = useCallback(
    (expr: string) => goToState(applySavedFilter(state, expr)),
    [goToState, state],
  );
  // Retire tout le filtre avancé (puce d'expression → état sans `expr`).
  const clearExpr = useCallback(() => goToState({ ...state, expr: undefined }), [goToState, state]);
  // Remplace l'expression (retrait d'un terme d'un OU de premier niveau).
  const changeExpr = useCallback(
    (expr: string) => goToState({ ...state, expr }),
    [goToState, state],
  );

  // « Enregistrer le filtre actuel… » : l'`expr` enregistré = `state.expr` s'il existe,
  // sinon l'expression dérivée des axes simples (`galleryStateToExprText`). Ouvre la saisie
  // nom + icône (le dialogue gère le cas « rien à enregistrer »).
  const openSaveCurrentFilter = useCallback(() => {
    const expr = hasExprFilter(state) ? (state.expr ?? '') : galleryStateToExprText(state);
    setSaveExprError(undefined);
    setSaveExprTarget(expr);
  }, [state]);

  // « Enregistrer sous… » depuis l'éditeur : enregistre l'expression canonique fournie.
  const openSaveExpr = useCallback((expr: string) => {
    setSaveExprError(undefined);
    setSaveExprTarget(expr);
  }, []);

  // Crée le filtre (POST) → l'ajoute à la liste ; 409 = nom déjà pris (message amical).
  const confirmSaveFilter = useCallback(
    async (name: string, icon: SavedFilterIcon | null) => {
      if (saveExprTarget === null || !libraryId) return;
      setSaveExprBusy(true);
      setSaveExprError(undefined);
      try {
        const created = await createSavedFilter(libraryId, {
          name,
          expr: saveExprTarget,
          ...(icon !== null ? { icon } : {}),
        });
        recordAction('savedFilter.create');
        setSavedFilters((prev) => [...prev, created]);
        setSaveExprTarget(null);
      } catch (err) {
        setSaveExprError(
          err instanceof ApiError && err.status === 409
            ? t('savedFilters.errorNameTaken')
            : t('savedFilters.errorSave'),
        );
      } finally {
        setSaveExprBusy(false);
      }
    },
    [saveExprTarget, libraryId, t],
  );

  // Changement de tri / de sens depuis le menu « Trier & filtrer ».
  //
  // Le choix est PERSISTÉ dans l'emplacement du contexte courant (accueil ou navigation)
  // en plus de partir dans l'URL : c'est ce qui le rend « collant » d'un dossier à
  // l'autre — une carte de dossier navigue vers `/dossiers?dossier=<X>` (aucun `tri`),
  // donc vers la préférence. Seul `tri=<clé>` part dans l'URL — `tri=random` suffit à
  // porter « l'ordre est aléatoire », la graine étant celle du COMPTE (ré-injectée au
  // prochain rendu par le résolveur). Aucune graine à traîner dans l'état sérialisé.
  //
  // La préférence n'est écrite QUE d'ici, sur ACTION EXPLICITE : ni la lecture de l'URL
  // ni le résolveur n'y touchent, sans quoi un lien reçu portant `?tri=filename`
  // écraserait la préférence de son destinataire.
  //
  // ORDRE VOLONTAIRE — on navigue AVANT de persister. `minimalGalleryLink` interroge la
  // préférence ENCORE EN VIGUEUR : le nouveau tri en diverge forcément, l'URL cible
  // diffère donc toujours de l'URL courante et la grille se recharge. Persister d'abord
  // aurait pu produire l'URL COURANTE (le nouveau tri étant devenu le défaut) — une
  // navigation sans changement d'href, donc une grille qui ne se retrie pas.
  const rememberSort = useCallback(
    (next: GalleryState) => storeSortPref(sortScope, { sort: next.sort, dir: next.dir }),
    [sortScope],
  );

  // CHANGER DE CRITÈRE REPOSE LE SENS CONVENTIONNEL de ce critère (`defaultDirForSort` :
  // noms en A→Z, dates et mesures du plus récent/grand d'abord) — comme le fait déjà la
  // liste « Ordre par défaut » des Paramètres, et comme le font l'Explorateur et le Finder
  // quand on change de colonne. Sans ça, passer d'« Aléatoire » (dont le sens vaut `desc`
  // par convention, mais ne veut rien dire pour un ordre pseudo-uniforme) à « Nom de
  // fichier » donnait un Z→A que personne n'a demandé. Le défaut était déjà là avant que
  // le tri soit COLLANT ; il devenait gênant en le devenant, puisqu'il ne disparaissait
  // plus au rechargement.
  //
  // Re-choisir le critère DÉJÀ actif ne touche pas au sens : ce n'est pas un changement de
  // colonne, et écraser un `asc` délibérément posé dans « Ordre » serait une régression.
  const onSortChange = useCallback(
    (sort: typeof state.sort) => {
      const dir = sort === state.sort ? state.dir : defaultDirForSort(sort);
      const next: GalleryState = { ...state, sort, dir };
      goToState(next);
      rememberSort(next);
    },
    [goToState, rememberSort, state],
  );

  const onDirChange = useCallback(
    (dir: typeof state.dir) => {
      const next: GalleryState = { ...state, dir };
      goToState(next);
      rememberSort(next);
    },
    [goToState, rememberSort, state],
  );

  // « Re-mélanger » : régénère la graine du COMPTE côté serveur (persistée → nouvel ordre
  // propagé à toutes les machines/sessions au prochain chargement). AUCUNE NAVIGATION :
  // `reshuffleRandomSeed` met à jour `user.randomSeed` dans le provider, donc `accountSeed`,
  // donc `state.seed` puis les filtres — la grille se recharge par cette seule dépendance et
  // l'URL ne bouge pas (elle ne porte plus la graine depuis le LOT C1).
  //
  // GARDE DE DOUBLE-SOUMISSION (audit F9) : chaque clic régénère la graine PERSISTÉE
  // côté serveur — sans garde, un double-clic (ou un rebond de bouton) faisait sauter
  // l'ordre deux fois de suite. Le `ref` est posé synchronement ; l'état pilote le
  // `disabled` du bouton.
  //
  // ÉCHEC REMONTÉ (audit F10) : avant, un `catch {}` rendait un « Re-mélanger » raté
  // INDISCERNABLE d'un no-op. On affiche désormais une alerte non bloquante et on
  // conserve l'ordre courant.
  const reshufflingRef = useRef(false);
  const [reshuffleBusy, setReshuffleBusy] = useState(false);
  const [reshuffleError, setReshuffleError] = useState<string | undefined>(undefined);

  const onReshuffle = useCallback(() => {
    if (reshufflingRef.current) return;
    reshufflingRef.current = true;
    setReshuffleBusy(true);
    setReshuffleError(undefined);
    void (async () => {
      try {
        await reshuffleRandomSeed();
      } catch {
        setReshuffleError(t('sort.reshuffleError'));
      } finally {
        reshufflingRef.current = false;
        setReshuffleBusy(false);
      }
    })();
  }, [reshuffleRandomSeed, t]);

  // Ouvre la modale de déplacement : soit toutes les vidéos avec son (résolution
  // serveur), soit la sélection courante de tuiles.
  const openMoveWithAudio = useCallback(() => setMoveTarget({ hasAudio: true }), []);
  const openMoveSelection = useCallback(() => {
    // Garde de sélection vide, comme ses jumelles (compresser/pivoter/supprimer) : le
    // mode sélection peut être armé AVANT qu'une tuile ne soit cochée.
    if (selected.size === 0) return;
    setMoveTarget({ assetIds: [...selected] });
  }, [selected]);

  // Un déplacement enfilé : ferme la modale, vide la sélection, rafraîchit les DOSSIERS
  // (les chemins changent). Ni la taxonomie ni les compteurs ne bougent : ce sont les
  // mêmes assets, dans les mêmes types, avec les mêmes tags. La progression se suit dans
  // la cloche.
  const onMoveQueued = useCallback(() => {
    setSelected(new Set());
    bumpFolders();
  }, [bumpFolders]);

  // Compression SANS PERTE : ouvre la modale (capture les ids courants). Ferme = annule.
  const openCompressSelection = useCallback(() => {
    if (selected.size === 0) return;
    setCompressTarget([...selected]);
  }, [selected]);
  // Un job compress enfilé : ferme la modale + vide la sélection (progression dans la
  // cloche). Les pixels/identity_hash sont inchangés → pas de purge de vignettes. AUCUNE
  // facette ni aucun compteur n'est concerné (mêmes fichiers, mêmes dossiers, mêmes
  // tags) : seuls les octets changent → rien à invalider.
  const onCompressQueued = useCallback(() => {
    setSelected(new Set());
  }, []);

  // Rotation « Pivoter » : ouvre la modale (capture les ids courants). Ferme = annule.
  const openRotateSelection = useCallback(() => {
    if (selected.size === 0) return;
    setRotateTarget([...selected]);
  }, [selected]);
  // Un job rotate enfilé : ferme la modale + vide la sélection (progression dans la cloche).
  // AUCUNE facette ni aucun compteur n'est concerné (mêmes fichiers, mêmes dossiers,
  // mêmes tags) → rien à invalider ici. La grille se rafraîchit quand le job aboutit
  // (signal `gallery-refresh`), ce qui fait apparaître le `rev` frais des vignettes.
  const onRotateQueued = useCallback(() => {
    setSelected(new Set());
  }, []);

  // Un téléversement réussi (XPHOTO-16) : rafraîchit compteurs + facettes IMMÉDIATEMENT,
  // ET arme l'observation de fin de scan (`markScanPending`). Le téléversement enfile côté
  // serveur un job `scan` qui INDEXE les nouveaux fichiers : en l'armant comme un scan
  // lancé manuellement, la grille se recharge automatiquement quand CE scan se termine →
  // les photos téléversées apparaissent sans F5 (avec repli par délai du hook si le scan
  // est trop rapide pour être vu actif). Sa progression se suit dans la cloche.
  const onUploaded = useCallback(() => {
    // Nouveaux fichiers → l'arborescence peut gagner un dossier et les compteurs montent.
    // La taxonomie, elle, ne bouge pas (un fichier téléversé n'a aucun tag).
    bumpFolders();
    bumpCounts();
    markScanPending();
  }, [bumpFolders, bumpCounts, markScanPending]);

  // Une correction d'extensions enfilée : les fichiers renommés gardent le même asset,
  // seuls leurs CHEMINS changent → facette Dossiers. Ni taxonomie ni compteurs. La
  // progression du job `rename_extensions` se suit dans la cloche.
  const onExtensionsFixed = useCallback(() => {
    bumpFolders();
  }, [bumpFolders]);

  // Accès ÉCRITURE à la bibliothèque — source UNIQUE de vérité côté SPA (`useCanWrite`,
  // alignée sur `AccessControl` du serveur : superadmin OU rôle `admin` OU propriétaire
  // d'une bibliothèque). Un grantee lecture (rôle `user`, `libraryId` null) ne peut
  // rien muter → toutes les affordances d'écriture de la barre d'outils sont MASQUÉES.
  const canWrite = useCanWrite();

  // --- ÉCRITURES SUR LES DOSSIERS (XPHOTO-32, lot 4) -----------------------
  // Créer / renommer / déplacer un dossier. Une seule modale, paramétrée par le geste.
  const [folderOp, setFolderOp] = useState<FolderOpTarget | null>(null);
  const openFolderOp = useCallback((kind: FolderOpKind, path: string) => {
    setFolderOp({ kind, path });
  }, []);

  /**
   * Après une écriture RÉUSSIE. Trois choses, et aucune n'est optionnelle :
   *
   *  1. INVALIDER les caches de dossiers — `bumpFolders` recharge l'arbre du niveau
   *     courant ET appelle `invalidateAllFolders()` (le sélecteur de destination
   *     partagé). Une carte périmée après un renommage serait un bug visible dans la
   *     seconde.
   *  2. RECHARGER la grille de photos : les `rel_path` ont changé sous elle.
   *  3. SUIVRE le dossier si l'on était DEDANS. Renommer le dossier ouvert laisserait
   *     l'URL sur un chemin qui n'existe plus — donc un écran vide, sans erreur ni
   *     explication. On navigue vers le nouveau chemin ; si c'est un ANCÊTRE du dossier
   *     ouvert qui a bougé, on recompose le chemin courant sur le nouveau préfixe.
   *     APRÈS UNE SUPPRESSION (lot 5), il n'y a AUCUN sous-chemin à recomposer — le
   *     sous-arbre entier a disparu : on remonte au PARENT, que le serveur a mis dans
   *     `result.path`. Recomposer donnerait un dossier qui n'existe pas.
   *
   * On diffuse aussi aux AUTRES onglets (`broadcastGalleryRefresh`) : cette mutation ne
   * passe par aucun job, donc rien d'autre ne les préviendrait (audit A7).
   */
  const onFolderOpDone = useCallback(
    (result: FolderOpResult, kind: FolderOpKind) => {
      bumpFolders();
      assets.reload();
      broadcastGalleryRefresh();
      const from = result.previousPath;
      if (from !== null && (folderPath === from || folderPath.startsWith(`${from}/`))) {
        navigate(
          folderLinkFor(
            kind === 'delete' ? result.path : result.path + folderPath.slice(from.length),
          ),
        );
      }
    },
    [bumpFolders, assets, folderPath, navigate],
  );

  // Corbeille (Lot 1) : ouvre la confirmation « mettre la sélection à la poubelle »
  // (capture les ids courants). Ferme = annule (jamais pendant un appel réseau).
  const openDeleteSelection = useCallback(() => {
    if (selected.size === 0) return;
    setDeleteError(undefined);
    setDeleteTarget([...selected]);
  }, [selected]);
  const closeDeleteSelection = useCallback(() => {
    setDeleteTarget(null);
    setDeleteError(undefined);
  }, []);

  // Confirmation : soft-delete (suppression LOGIQUE réversible, aucun fichier touché) →
  // vide la sélection + rafraîchit compteurs/facettes + recharge la grille (les assets
  // supprimés disparaissent de toutes les vues). Un 403 (grantee lecture) ou autre échec
  // affiche un message dans la modale (non bloquant).
  const confirmDeleteSelection = useCallback(async () => {
    if (deleteTarget === null || !libraryId) return;
    setDeleteBusy(true);
    setDeleteError(undefined);
    try {
      await softDeleteAssets(libraryId, deleteTarget);
      recordAction('trash.softDelete');
      setDeleteTarget(null);
      setSelected(new Set());
      // Des assets disparaissent : dossiers (un dossier peut se vider), usages de tags et
      // compteurs sont TOUS concernés.
      bumpAllFacets();
      assets.reload();
      // SYNCHRO INTER-ONGLETS (audit A7) : la mise à la corbeille ne passe PAS par un
      // job, donc rien ne la signalait aux autres onglets — ils gardaient les vignettes
      // supprimées, et un clic dessus rendait un 404. On diffuse SANS notifier localement
      // (cet onglet vient déjà de recharger juste au-dessus).
      broadcastGalleryRefresh();
    } catch (err) {
      setDeleteError(
        err instanceof ApiError && err.status === 403
          ? t('trash.errorForbidden')
          : t('trash.error'),
      );
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteTarget, libraryId, bumpAllFacets, assets, t]);

  // Notation de masse (LOT 3) : ouvre la modale de sélection d'étoiles (capture les ids
  // courants). Fermer = annule (jamais pendant un appel réseau).
  const openRateSelection = useCallback(() => {
    if (selected.size === 0) return;
    setRateError(undefined);
    setRateTarget([...selected]);
  }, [selected]);
  const closeRateSelection = useCallback(() => {
    setRateTarget(null);
    setRateError(undefined);
  }, []);

  // « Appliquer » : fixe la note choisie sur toute la sélection (mutation DB synchrone) →
  // vide la sélection + met à jour les tuiles chargées (tri « mieux notées ») sans rechargement.
  // Un 403 (grantee lecture) ou autre échec affiche un message dans la modale (non bloquant).
  const confirmRateSelection = useCallback(
    async (rating: Rating) => {
      if (rateTarget === null || !libraryId) return;
      setRateBusy(true);
      setRateError(undefined);
      try {
        await setRatingForSelection(libraryId, rateTarget, rating);
        recordAction('bulkRating.apply');
        // Reflète la note sur les tuiles chargées (celles de la sélection).
        for (const id of rateTarget) {
          const idx = assets.indexOfLoaded(id);
          if (idx >= 0) assets.patchAsset(idx, { rating });
        }
        setRateTarget(null);
        setSelected(new Set());
        // AUCUNE invalidation (audit A4) : la note n'apparaît ni dans les facettes
        // (dossiers/tags) ni dans les compteurs, et les tuiles chargées viennent d'être
        // mises à jour localement juste au-dessus.
      } catch (err) {
        setRateError(
          err instanceof ApiError && err.status === 403
            ? t('bulkRating.errorForbidden')
            : t('bulkRating.error'),
        );
      } finally {
        setRateBusy(false);
      }
    },
    [rateTarget, libraryId, assets, t],
  );

  // Ajout de tags EXISTANTS en masse (LOT 4) : ouvre la modale de choix de tags (capture
  // les ids courants). Fermer = annule (jamais pendant un appel réseau).
  const openAddTagSelection = useCallback(() => {
    if (selected.size === 0) return;
    setTagError(undefined);
    setTagTarget([...selected]);
  }, [selected]);
  const closeAddTagSelection = useCallback(() => {
    setTagTarget(null);
    setTagError(undefined);
  }, []);

  // « Appliquer » : ajoute les tags choisis (EXISTANTS) à toute la sélection (mutation DB
  // synchrone) → vide la sélection + rafraîchit les facettes Tags (nouveaux usages). Un
  // 403 (grantee lecture) ou autre échec affiche un message dans la modale (non bloquant).
  const confirmAddTagSelection = useCallback(
    async (tagIds: string[]) => {
      if (tagTarget === null || !libraryId || tagIds.length === 0) return;
      setTagBusy(true);
      setTagError(undefined);
      try {
        await addTagsToSelection(libraryId, tagTarget, tagIds);
        recordAction('bulkTag.apply');
        setTagTarget(null);
        setSelected(new Set());
        // Seuls les USAGES de tags changent (facette Tags).
        bumpTags();
      } catch (err) {
        setTagError(
          err instanceof ApiError && err.status === 403
            ? t('bulkTag.errorForbidden')
            : t('bulkTag.error'),
        );
      } finally {
        setTagBusy(false);
      }
    },
    [tagTarget, libraryId, bumpTags, t],
  );

  // « Retirer » : détache les tags choisis de toute la sélection (mutation DB synchrone)
  // → vide la sélection + rafraîchit les facettes Tags (usages retombés). Un 403 (grantee
  // lecture) ou autre échec affiche un message dans la modale (non bloquant).
  const confirmRemoveTagSelection = useCallback(
    async (tagIds: string[]) => {
      if (tagTarget === null || !libraryId || tagIds.length === 0) return;
      setTagBusy(true);
      setTagError(undefined);
      try {
        await removeTagsFromSelection(libraryId, tagTarget, tagIds);
        recordAction('bulkTag.remove');
        setTagTarget(null);
        setSelected(new Set());
        // Seuls les USAGES de tags changent (facette Tags).
        bumpTags();
      } catch (err) {
        setTagError(
          err instanceof ApiError && err.status === 403
            ? t('bulkTag.errorForbidden')
            : t('bulkTag.removeError'),
        );
      } finally {
        setTagBusy(false);
      }
    },
    [tagTarget, libraryId, bumpTags, t],
  );

  // Aiguillage ajouter/retirer selon le mode courant de la modale.
  const confirmTagSelection = useCallback(
    (tagIds: string[], mode: BulkTagMode) => {
      if (mode === 'remove') void confirmRemoveTagSelection(tagIds);
      else void confirmAddTagSelection(tagIds);
    },
    [confirmAddTagSelection, confirmRemoveTagSelection],
  );

  // Acceptation en masse des suggestions IA (Lot 2) : ouvre la confirmation légère
  // (capture les ids courants). Fermer = annule (jamais pendant un appel réseau).
  const openAcceptAiTags = useCallback(() => {
    if (selected.size === 0) return;
    setAcceptAiError(undefined);
    setAcceptAiTarget([...selected]);
  }, [selected]);
  const closeAcceptAiTags = useCallback(() => {
    setAcceptAiTarget(null);
    setAcceptAiError(undefined);
  }, []);

  // Confirmation : promeut les suggestions IA `proposed`→`validated` de la sélection
  // (mutation DB synchrone ; NE change PAS `tagged` → les photos restent « À valider (IA) »)
  // → vide la sélection + rafraîchit compteurs/facettes (nouveaux usages validés). Un 403
  // (grantee lecture) ou autre échec affiche un message dans la modale (non bloquant).
  const confirmAcceptAiTags = useCallback(async () => {
    if (acceptAiTarget === null || !libraryId) return;
    setAcceptAiBusy(true);
    setAcceptAiError(undefined);
    try {
      await acceptAiTagsForSelection(libraryId, acceptAiTarget);
      recordAction('ai.acceptTags');
      setAcceptAiTarget(null);
      setSelected(new Set());
      // Promotion `proposed`→`validated` : les usages de tags bougent. `tagged` est
      // INCHANGÉ (les photos restent « À valider (IA) ») → compteurs intacts.
      bumpTags();
    } catch (err) {
      setAcceptAiError(
        err instanceof ApiError && err.status === 403
          ? t('bulkTag.acceptAiForbidden')
          : t('bulkTag.acceptAiError'),
      );
    } finally {
      setAcceptAiBusy(false);
    }
  }, [acceptAiTarget, libraryId, bumpTags, t]);

  // « Réviser / valider les tags » : ouvre le TAGUEUR par photo (visionneuse en mode « tag »)
  // sur la sélection courante, en repartant de la 1re photo. La navigation reste bornée à
  // cette liste ; à la fermeture on revient à la galerie courante (filtres préservés).
  const onTagSelection = useCallback(() => {
    const ids = [...selected];
    if (ids.length === 0) return;
    openViewerTagging(navigate, ids, ids[0]!, galleryHref);
  }, [selected, navigate, galleryHref]);

  // « Diaporama » (menu « Actions… ») : ouvre la visionneuse en mode SÉRIE sur les photos
  // SÉLECTIONNÉES et démarre la lecture auto d'emblée. L'ordre suit la sélection (ordre
  // de la grille via « Tout sélectionner », sinon ordre des clics — même choix que le tagueur).
  const onSlideshowSelection = useCallback(() => {
    const ids = [...selected];
    if (ids.length === 0) return;
    openViewerSlideshow(navigate, ids, ids[0]!, galleryHref);
  }, [selected, navigate, galleryHref]);

  // « Réviser ces N photos » (vue « À valider (IA) ») : ouvre le TAGUEUR sur TOUT
  // l'ensemble filtré courant EN UN CLIC. Récupère tous les ids du groupe (mêmes filtres
  // que la grille, ordre de la grille — via `fetchAllAssetIds`) puis lance le tagueur
  // depuis la 1re photo. Évite le détour « Tout sélectionner » → « Actions… ».
  const reviewPendingAi = useCallback(async () => {
    if (!libraryId || reviewBusy) return;
    setReviewBusy(true);
    setReviewError(undefined);
    try {
      const ids = await fetchAllAssetIds(libraryId, filters);
      if (ids.length === 0) {
        setReviewError(t('gallery.reviewEmpty'));
        return;
      }
      openViewerTagging(navigate, ids, ids[0]!, galleryHref);
    } catch {
      setReviewError(t('gallery.reviewError'));
    } finally {
      setReviewBusy(false);
    }
  }, [libraryId, reviewBusy, filters, navigate, galleryHref, t]);

  // Tagging IA (T5 lot C) : propose des tags par le LLM pour la SÉLECTION courante si
  // non vide, sinon toute la bibliothèque. Courte confirmation ; la progression se
  // suit dans la cloche (les propositions se valident au lot D). Un 409 sans
  // fournisseur remonte `'no-provider'` → le menu Outils affiche le bon message.
  const onTagAi = useCallback((): Promise<'ok' | 'no-provider' | 'disabled' | 'error'> => {
    if (!libraryId) return Promise.resolve('error');
    const ids = [...selected];
    const message =
      ids.length > 0
        ? interpolate(t('tools.tagAiConfirmSelection'), { count: ids.length }, locale)
        : t('tools.tagAiConfirm');
    // Ouvre NOTRE modale de confirmation et rend une Promesse résolue par le choix de
    // l'utilisateur (annuler → `'ok'`, aucune action ; confirmer → enfil → outcome).
    return new Promise((resolve) => {
      aiTagResolveRef.current = resolve;
      setAiTagConfirm({ ids, message });
    });
  }, [libraryId, selected, t]);

  // Annulation de la confirmation IA : ferme la modale et résout `'ok'` (rien enfilé).
  const cancelTagAi = useCallback(() => {
    setAiTagConfirm(null);
    aiTagResolveRef.current?.('ok');
    aiTagResolveRef.current = null;
  }, []);

  // Confirmation IA : enfile le job (sélection ou toute la bibliothèque) puis résout
  // l'outcome (`no-provider` → alerte inline de la Toolbar). Ferme la modale ensuite.
  const confirmTagAi = useCallback(async () => {
    const target = aiTagConfirm;
    if (!libraryId || target === null) return;
    setAiTagBusy(true);
    let outcome: 'ok' | 'no-provider' | 'disabled' | 'error';
    try {
      await enqueueAiTag(
        libraryId,
        target.ids.length > 0
          ? { scope: 'selection', assetIds: target.ids, dryRun: false }
          : { scope: 'all', dryRun: false },
      );
      recordAction('ai.tag.enqueue');
      outcome = 'ok';
    } catch (err) {
      if (err instanceof AiApiError && err.code === 'AI_PROVIDER_MISSING') outcome = 'no-provider';
      else if (err instanceof AiApiError && err.code === 'AI_DISABLED') outcome = 'disabled';
      else outcome = 'error';
    }
    setAiTagBusy(false);
    setAiTagConfirm(null);
    aiTagResolveRef.current?.(outcome);
    aiTagResolveRef.current = null;
  }, [aiTagConfirm, libraryId]);

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Maj-clic (XPHOTO-32 lot 2) : la plage résolue par la grille s'AJOUTE à la sélection
  // — elle ne la remplace pas (on enchaîne plusieurs plages pour trier un dossier).
  const selectRange = useCallback((ids: readonly string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);

  // Vide la sélection courante (« Tout désélectionner », croix du compteur) — SANS
  // quitter le mode sélection : on décoche pour recommencer, pas pour sortir.
  const clearSelection = useCallback(() => {
    setSelected(new Set());
    setSelectAllError(undefined);
  }, []);

  /**
   * RÈGLE UNIQUE du mode sélection (v1.41.0) : **armé explicitement OU sélection non
   * vide**. Une seule formule pour les deux mondes, sans jamais brancher sur le type de
   * pointeur :
   *  - au TACTILE, rien n'est coché et le mode est éteint → grille propre, aucune case,
   *    un tap ouvre la photo ; le bouton « Sélectionner » arme le mode ;
   *  - au SURVOL (desktop), cocher une case rend `selected.size > 0` → on entre dans le
   *    mode tout seul, sans avoir à connaître le bouton.
   */
  // Ce que l'utilisateur a DEMANDÉ : mode explicite, ou sélection non vide. Distinct de
  // `selectionMode` (plus bas), qui est ce que l'INTERFACE rend réellement.
  const selectionActive = explicitSelectionMode || selected.size > 0;

  /**
   * Bascule du bouton « Sélectionner » / « Terminé ».
   *
   * SORTIR VIDE TOUJOURS LA SÉLECTION. Sans cela, on pourrait laisser une sélection non
   * vide INVISIBLE (plus aucune case affichée) : une action de masse porterait alors sur
   * des photos que l'utilisateur ne voit plus cochées. Le mode et la sélection sortent
   * donc ensemble.
   */
  const toggleSelectionMode = useCallback(() => {
    if (selectionActive) {
      setExplicitSelectionMode(false);
      clearSelection();
      return;
    }
    setExplicitSelectionMode(true);
  }, [selectionActive, clearSelection]);

  // « Tout sélectionner » : récupère côté serveur TOUS les ids du GROUPE COURANT (mêmes
  // filtres que la grille : dossier/tag/à-traiter/type/son/recherche…) — pas seulement
  // les ~100 tuiles chargées — et les coche en une fois. Fonctionne sur des milliers
  // d'assets. Un échec (403 grantee, réseau) affiche un message non bloquant.
  const selectAll = useCallback(async () => {
    if (!libraryId || selectAllBusy) return;
    setSelectAllBusy(true);
    setSelectAllError(undefined);
    try {
      const ids = await fetchAllAssetIds(libraryId, filters);
      setSelected(new Set(ids));
    } catch (err) {
      setSelectAllError(
        err instanceof ApiError && err.status === 403
          ? t('select.selectAllForbidden')
          : t('select.selectAllError'),
      );
    } finally {
      setSelectAllBusy(false);
    }
  }, [libraryId, selectAllBusy, filters, t]);

  // « Télécharger la sélection » : demande au serveur l'archive ZIP des originaux de la
  // sélection (POST du blob), puis déclenche un téléchargement navigateur natif via un
  // `<a download>` éphémère. On NE VIDE PAS la sélection (l'utilisateur peut vouloir
  // enchaîner d'autres actions). Un 403 (grantee lecture) ou autre échec affiche un
  // message non bloquant. La préparation peut être lente → état occupé pendant l'appel.
  const downloadSelection = useCallback(async () => {
    if (!libraryId || downloadBusy || selected.size === 0) return;
    setDownloadBusy(true);
    setDownloadError(undefined);
    try {
      const blob = await downloadSelectionZip(libraryId, [...selected]);
      recordAction('selection.download');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'xphoto-selection.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(
        err instanceof ApiError && err.status === 403
          ? t('select.downloadForbidden')
          : t('select.downloadError'),
      );
    } finally {
      setDownloadBusy(false);
    }
  }, [libraryId, downloadBusy, selected, t]);

  // Une note posée depuis la visionneuse met à jour la tuile chargée (tri « mieux
  // notées »). Pas de rafraîchissement des compteurs (la note ne change pas d'onglet).
  const handleViewerRatingChange = useCallback(
    (assetId: string, nextRating: number) => {
      const idx = assets.indexOfLoaded(assetId);
      if (idx >= 0) assets.patchAsset(idx, { rating: nextRating });
    },
    [assets],
  );

  // Ouvre la visionneuse sur un asset (index de grille) → route `/photo/:id`, en
  // mémorisant la galerie courante (chemin + query) pour un retour aux filtres.
  const openAsset = useCallback(
    (index: number) => {
      const asset = assets.getAsset(index);
      if (!asset) return;
      navigate(`/photo/${asset.id}`, { state: { back: galleryHref } });
    },
    [assets, navigate, galleryHref],
  );

  // Contexte partagé avec la route visionneuse (préc./suiv. dans la liste chargée).
  const viewerContext = useMemo<GalleryViewerContextValue>(
    () => ({
      total: assets.total,
      getAsset: assets.getAsset,
      indexOfId: assets.indexOfLoaded,
      ensureRange: assets.ensureRange,
      onRatingChange: handleViewerRatingChange,
    }),
    [assets, handleViewerRatingChange],
  );

  const tagItems: FacetItem[] = useMemo(
    () => (tags.data ?? []).map((tg) => ({ value: tg.id, label: tg.label, count: tg.count })),
    [tags.data],
  );

  const total = assets.total;

  // La sélection (et donc « Tout sélectionner ») n'a de sens que quand une GRILLE
  // d'images est affichée. En mode explorateur (onglet Dossiers), elle l'est dès qu'il y a
  // des photos DIRECTES dans le dossier courant (`total > 0`) ; s'il n'y a que des cartes
  // de sous-dossiers, on masque le contrôle. Toutes les autres vues (galerie, tag,
  // à-traiter, lien profond EXIF) affichent une grille → sélection disponible.
  const selectionAvailable = !inFolderExplorer || total > 0;

  // Ce que l'interface REND : le mode voulu, borné par sa disponibilité.
  //
  // Le mode SURVIT à la navigation. En atterrissant sur une vue SANS grille d'images —
  // uniquement des cartes de sous-dossiers — la barre de sélection n'est pas rendue, donc
  // le bouton « Terminé » non plus : le mode resterait ARMÉ SANS AUCUNE SORTIE VISIBLE.
  // On l'éteint par DÉRIVATION plutôt que par un effet correctif, si bien que cet état
  // n'existe jamais — au lieu d'exister un rendu puis d'être rattrapé. Le choix explicite
  // de l'utilisateur est CONSERVÉ et reprend effet dès qu'une grille réapparaît ; rien ne
  // peut avoir été sélectionné entre-temps, puisqu'aucune tuile n'était affichée.
  const selectionMode = selectionAvailable && selectionActive;

  // Libellé de la puce de filtre EXIF actif (icône + texte, jamais la couleur seule).
  const exifFilterLabel = state.hasExifField
    ? interpolate(t('gallery.activeExifField'), { field: state.hasExifField })
    : state.publishState === 'outOfSync'
      ? t('gallery.activePublishOutOfSync')
      : state.publishState === 'inSync'
        ? t('gallery.activePublishInSync')
        : null;

  return (
    <GalleryViewerContext.Provider value={viewerContext}>
      {/* Contenu de la galerie rendu DANS la zone `main` de la coquille partagée
          (`AppChrome`) — l'en-tête (fil d'Ariane + actions) est persistant au-dessus. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {/* Titre de niveau 1 de l'écran. Le wordmark ayant été retiré de l'en-tête, la
            coquille n'en fournit plus : sans lui, la galerie n'avait que des `<h2>` et
            la navigation par titres d'un lecteur d'écran démarrait au niveau 2. Visuel
            déjà porté par le fil d'Ariane → `sr-only`. */}
        <h1 className="sr-only">{t('nav.gallery')}</h1>
        <Toolbar
          total={total}
          selectedCount={selected.size}
          /* Ce que « Partager » proposera : la SÉLECTION si elle est non vide (c'est
             alors le geste explicite le plus récent de l'utilisateur), sinon le DOSSIER
             courant — `''` sur l'accueil, soit toute la bibliothèque qu'il peut voir. */
          shareTarget={
            selected.size > 0
              ? { kind: 'selection', assetIds: [...selected] }
              : { kind: 'folder', folderPath: state.folder ?? '' }
          }
          shareTitle={
            selected.size > 0
              ? t('share.view')
              : (state.folder ?? '').split('/').pop() || t('nav.gallery')
          }
          onClearSelection={clearSelection}
          selectionMode={selectionMode}
          onToggleSelectionMode={toggleSelectionMode}
          selectionAvailable={selectionAvailable}
          onSelectAll={() => {
            void selectAll();
          }}
          selectAllBusy={selectAllBusy}
          selectAllError={selectAllError}
          sort={state.sort}
          onSortChange={onSortChange}
          dir={state.dir}
          onDirChange={onDirChange}
          onReshuffle={onReshuffle}
          reshuffleBusy={reshuffleBusy}
          reshuffleError={reshuffleError}
          mediaType={state.mediaType ?? null}
          onMediaTypeChange={(mediaType) =>
            goToState({ ...state, mediaType: mediaType ?? undefined })
          }
          imageCount={counts.data?.byKind.image ?? 0}
          animatedImageCount={counts.data?.byKind.animatedImage ?? 0}
          videoCount={counts.data?.byKind.video ?? 0}
          untaggedOnly={state.tagged === false && !state.pendingAiTags}
          onUntaggedOnlyChange={(untagged) =>
            // « Non taguées » et « À valider (IA) » sont mutuellement exclusives : activer
            // l'une efface l'autre.
            goToState({
              ...state,
              tagged: untagged ? false : undefined,
              pendingAiTags: undefined,
            })
          }
          aiReviewOnly={state.pendingAiTags === true}
          onAiReviewOnlyChange={(review) =>
            goToState({
              ...state,
              pendingAiTags: review ? true : undefined,
              tagged: undefined,
            })
          }
          savedFilters={savedFilters}
          savedFiltersLoading={savedFiltersLoading}
          savedFiltersError={savedFiltersError}
          activeExpr={state.expr}
          onApplySavedFilter={applyExpr}
          onSaveCurrentFilter={openSaveCurrentFilter}
          onEditExpr={() => setExprEditorOpen(true)}
          onManageFilters={() => setManageOpen(true)}
          onOpenAdvancedSearch={() => setAdvancedOpen(true)}
          advancedActive={hasAdvancedFilter(state) || state.search.trim().length > 0}
          density={density}
          onDensityChange={setDensity}
          withAudio={counts.data?.withAudio ?? 0}
          onMoveWithAudio={openMoveWithAudio}
          onMoveSelection={openMoveSelection}
          onCompressSelection={openCompressSelection}
          onRotateSelection={openRotateSelection}
          onRateSelection={openRateSelection}
          onAddTagSelection={openAddTagSelection}
          onTagSelection={onTagSelection}
          onSlideshowSelection={onSlideshowSelection}
          onAcceptAiTags={openAcceptAiTags}
          onDeleteSelection={openDeleteSelection}
          onDownloadSelection={() => {
            void downloadSelection();
          }}
          downloadBusy={downloadBusy}
          downloadError={downloadError}
          onScanQueued={markScanPending}
          scanActive={scanActive}
          onOpenExifTool={() => navigate('/outils/exif')}
          onOpenExifPublish={() => navigate('/outils/exif-publier')}
          onOpenDedup={() => navigate('/outils/dedup')}
          onOpenTaxonomy={() => navigate('/outils/taxonomie')}
          onOpenUpload={() => setUploadOpen(true)}
          onOpenFixExtensions={() => setFixExtensionsOpen(true)}
          onOpenCleanEmptyFolders={() => setCleanEmptyFoldersOpen(true)}
          canUpload={canWrite}
          canWrite={canWrite}
          onTagAi={onTagAi}
          aiEnabled={aiEnabled}
        />

        {/* Bandeau PROÉMINENT « Réviser ces N photos » — visible seulement sur la vue
            « À valider (IA) » (`?taguees=avalider`) quand il y a des photos. Ouvre le
            tagueur sur TOUT l'ensemble filtré EN UN CLIC (plus besoin de « Tout
            sélectionner » → « Actions… »). */}
        {state.pendingAiTags && total > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-brand bg-brand/5 px-3 py-2">
            <button
              type="button"
              onClick={() => {
                void reviewPendingAi();
              }}
              disabled={reviewBusy}
              className="flex h-11 items-center gap-1.5 rounded-md bg-brand px-4 text-sm font-medium text-brand-fg transition-colors hover:bg-brand/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 motion-reduce:transition-none"
            >
              <PencilSquareIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span>
                {reviewBusy ? t('gallery.reviewPreparing') : tp('gallery.reviewPending', total)}
              </span>
            </button>
            {reviewError && (
              <p role="alert" className="flex items-start gap-2 text-sm text-danger">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{reviewError}</span>
              </p>
            )}
          </div>
        )}

        {/* Puce du filtre EXIF actif (lien profond Outil EXIF) — clairement effaçable. */}
        {hasExifFilter(state) && (
          <div
            role="status"
            className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-fg"
          >
            <FunnelIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
            <span>{exifFilterLabel}</span>
            <button
              type="button"
              onClick={clearExifFilter}
              title={t('gallery.clearExifFilter')}
              aria-label={t('gallery.clearExifFilter')}
              className={`ml-auto inline-flex ${TOUCH_TARGET} items-center gap-1 rounded-md px-2 py-1 text-muted hover:bg-surface hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand`}
            >
              <XMarkIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t('gallery.clearFilter')}
            </button>
          </div>
        )}

        {/* Puces des critères de recherche actifs (nom de fichier / note min / tags) —
            chacune effaçable indépendamment. */}
        <AdvancedFilterChips
          filename={state.search}
          ratingMin={state.ratingMin}
          tagIds={state.tagIds}
          tagMode={state.tagMode}
          onClearFilename={clearAdvancedFilename}
          onClearRating={clearAdvancedRating}
          onClearTags={clearAdvancedTags}
        />

        {/* Puces de décomposition du FILTRE AVANCÉ actif (`state.expr`) — OU de premier
            niveau éclaté en puces effaçables, sinon une seule puce « Expression avancée ». */}
        <ExprFilterChips expr={state.expr} onClearExpr={clearExpr} onChangeExpr={changeExpr} />

        {/* Onglet Dossiers = navigation façon EXPLORATEUR : à CHAQUE niveau, un fil
            d'Ariane de dossiers cliquable + les CARTES de sous-dossiers directs + les
            photos DIRECTEMENT dans le dossier courant. Clé PAR CHEMIN → remonter/descendre
            remonte la vue (défilement réinitialisé, réserve restaurée par chemin). */}
        {inFolderExplorer ? (
          <FoldersTabView
            key={`folders:${folderPath}`}
            libraryId={libraryId}
            folderPath={folderPath}
            folders={folders}
            density={density}
            assets={assets}
            selected={selected}
            selectionMode={selectionMode}
            onToggleSelect={toggleSelect}
            onSelectRange={selectRange}
            onOpenAsset={openAsset}
            // Écritures de dossier (lot 4) : PASSÉES SEULEMENT si l'utilisateur a
            // l'accès écriture. En lecture seule, `undefined` ⇒ aucun bouton ni menu
            // n'est rendu (masquer, pas désactiver — discipline `useCanWrite`).
            onFolderAction={canWrite ? openFolderOp : undefined}
          />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
            {/* Panneau de facettes de l'onglet Tags (l'onglet Dossiers utilise désormais
              la grille de cartes ci-dessus, plus de liste). */}
            {state.tab === 'tags' && (
              <aside className="md:min-h-0 md:w-64 md:shrink-0 md:overflow-y-auto">
                <FacetPanel
                  title={t('tagsTab.title')}
                  items={tagItems}
                  loading={tags.loading}
                  errorCode={tags.errorCode}
                  emptyLabel={t('tagsTab.empty')}
                  emptyHint={t('tagsTab.emptyHint')}
                  selectTemplate={t('tagsTab.select')}
                  selectVar="label"
                  countLabel={(count) => tp('tagsTab.countLabel', count)}
                  selected={state.tag}
                  onSelect={(tag) => goToState({ ...state, tag })}
                />
              </aside>
            )}

            {/* Zone principale : grille (ou état vide / erreur / pas de bibliothèque).
              `min-h-0` autorise l'enfant scrollable (la grille virtualisée) à
              borner sa hauteur au conteneur au lieu de pousser la coquille. */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <GalleryBody
                libraryId={libraryId}
                assets={assets}
                density={density}
                selected={selected}
                selectionMode={selectionMode}
                onToggleSelect={toggleSelect}
                onSelectRange={selectRange}
                onOpenAsset={openAsset}
              />
            </div>
          </div>
        )}
      </div>

      {/* Visionneuse pilotée par la route (`/photo/:id`) — reste une surcouche
          plein écran par-dessus la galerie (route imbriquée via l'`<Outlet>`). */}
      <Outlet />

      {/* Écritures sur les dossiers (XPHOTO-32 lot 4) : créer / renommer / déplacer.
          SYNCHRONE — au retour, l'arborescence a déjà changé (aucun job, aucune cloche). */}
      {folderOp !== null && libraryId && (
        <Portal>
          <FolderOpModal
            libraryId={libraryId}
            target={folderOp}
            onClose={() => setFolderOp(null)}
            onDone={onFolderOpDone}
          />
        </Portal>
      )}

      {/* Modale de déplacement de fichiers (LOT 2) — portée hors de l'en-tête
          `backdrop-blur` pour un vrai plein écran. */}
      {moveTarget !== null && libraryId && (
        <Portal>
          <MoveToFolderModal
            libraryId={libraryId}
            target={moveTarget}
            onClose={() => setMoveTarget(null)}
            onQueued={onMoveQueued}
          />
        </Portal>
      )}

      {/* Modale de COMPRESSION SANS PERTE de la sélection — portée hors de l'en-tête
          `backdrop-blur`. Progression suivie dans la cloche. */}
      {compressTarget !== null && libraryId && (
        <Portal>
          <CompressModal
            libraryId={libraryId}
            assetIds={compressTarget}
            onClose={() => setCompressTarget(null)}
            onQueued={onCompressQueued}
          />
        </Portal>
      )}

      {/* Modale « Pivoter » la sélection (rotation par le tag EXIF Orientation) — portée
          hors de l'en-tête `backdrop-blur`. Progression suivie dans la cloche. */}
      {rotateTarget !== null && libraryId && (
        <Portal>
          <RotateModal
            libraryId={libraryId}
            assetIds={rotateTarget}
            onClose={() => setRotateTarget(null)}
            onRotateQueued={onRotateQueued}
          />
        </Portal>
      )}

      {/* Modale de TÉLÉVERSEMENT (XPHOTO-16) — portée hors de l'en-tête `backdrop-blur`.
          Défaut de dossier = dossier OUVERT (onglet Dossiers avec `?dossier`), sinon
          dernier utilisé / racine (résolu dans la modale). */}
      {uploadOpen && libraryId && (
        <Portal>
          <UploadModal
            libraryId={libraryId}
            currentFolder={state.tab === 'folders' ? state.folder : undefined}
            onClose={() => setUploadOpen(false)}
            onUploaded={onUploaded}
          />
        </Portal>
      )}

      {/* Modale « Corriger les extensions » — portée hors de l'en-tête `backdrop-blur`.
          Scan (lecture) puis job `rename_extensions` suivi dans la cloche. */}
      {fixExtensionsOpen && libraryId && (
        <Portal>
          <FixExtensionsModal
            libraryId={libraryId}
            onClose={() => setFixExtensionsOpen(false)}
            onQueued={onExtensionsFixed}
          />
        </Portal>
      )}

      {/* Modale « Nettoyer les dossiers vides » — portée hors de l'en-tête `backdrop-blur`.
          Aperçu (lecture) puis job `empty_folders_cleanup` suivi dans la cloche. AUCUN
          `onQueued` : les facettes Dossiers sont dérivées des assets, or un répertoire vide
          n'en porte aucun — il n'a jamais été affiché, sa suppression ne change donc rien
          à ce qui est à l'écran. */}
      {cleanEmptyFoldersOpen && libraryId && (
        <Portal>
          <CleanEmptyFoldersModal
            libraryId={libraryId}
            onClose={() => setCleanEmptyFoldersOpen(false)}
          />
        </Portal>
      )}

      {/* Panneau « Recherche » (nom de fichier / note min / types / tags) — porté hors de
          l'en-tête `backdrop-blur`. « Appliquer » pousse les critères dans l'état/URL galerie. */}
      {advancedOpen && libraryId && (
        <Portal>
          <AdvancedSearchModal
            libraryId={libraryId}
            initial={{
              filename: state.search,
              ratingMin: state.ratingMin,
              tagIds: state.tagIds,
              tagMode: state.tagMode,
            }}
            onApply={applyAdvanced}
            onClose={() => setAdvancedOpen(false)}
          />
        </Portal>
      )}

      {/* Éditeur d'EXPRESSION AVANCÉE (« Modifier l'expression… ») — porté hors de l'en-tête
          `backdrop-blur`. « Appliquer » pousse l'expression (état propre + `expr`) ;
          « Enregistrer sous… » ouvre la saisie nom + icône par-dessus. */}
      {exprEditorOpen && libraryId && (
        <Portal>
          <FilterExpressionModal
            libraryId={libraryId}
            initialExpr={state.expr ?? ''}
            onApply={(expr) => {
              applyExpr(expr);
              setExprEditorOpen(false);
            }}
            onSaveAs={openSaveExpr}
            onClose={() => setExprEditorOpen(false)}
          />
        </Portal>
      )}

      {/* Saisie NOM + ICÔNE d'un filtre à enregistrer (depuis le menu OU l'éditeur) —
          portée hors de l'en-tête `backdrop-blur`. 409 = nom déjà pris (message amical). */}
      {saveExprTarget !== null && (
        <Portal>
          <SaveFilterDialog
            expr={saveExprTarget}
            busy={saveExprBusy}
            error={saveExprError}
            onSave={(name, icon) => {
              void confirmSaveFilter(name, icon);
            }}
            onClose={() => setSaveExprTarget(null)}
          />
        </Portal>
      )}

      {/* Écran « Gérer mes filtres » (renommer / icône / supprimer / réordonner) — porté
          hors de l'en-tête `backdrop-blur`. Remonte la liste à jour pour garder le menu synchro. */}
      {manageOpen && libraryId && (
        <Portal>
          <ManageSavedFiltersModal
            libraryId={libraryId}
            filters={savedFilters}
            onChanged={setSavedFilters}
            onClose={() => setManageOpen(false)}
          />
        </Portal>
      )}

      {/* Modale de notation de masse (LOT 3) — portée hors de l'en-tête `backdrop-blur`. */}
      {rateTarget !== null && (
        <Portal>
          <BulkRatingModal
            count={rateTarget.length}
            busy={rateBusy}
            error={rateError}
            onApply={(rating) => {
              void confirmRateSelection(rating);
            }}
            onClose={closeRateSelection}
          />
        </Portal>
      )}

      {/* Modale de tags EN MASSE (LOT 4 + retrait) — portée hors de l'en-tête `backdrop-blur`.
          Mode « Ajouter » : taxonomie existante (aucune création). Mode « Retirer » : tags
          présents sur la sélection. */}
      {tagTarget !== null && libraryId && (
        <Portal>
          <BulkTagModal
            libraryId={libraryId}
            assetIds={tagTarget}
            busy={tagBusy}
            error={tagError}
            onApply={confirmTagSelection}
            onClose={closeAddTagSelection}
          />
        </Portal>
      )}

      {/* Confirmation « Accepter les suggestions IA » (Lot 2, mutation synchrone) —
          portée hors de l'en-tête `backdrop-blur`. Message léger ; les photos restent
          « À valider (IA) » jusqu'à finalisation. */}
      {acceptAiTarget !== null && (
        <Portal>
          <ConfirmModal
            title={t('bulkTag.acceptAiTitle')}
            confirmLabel={t('bulkTag.acceptAiConfirm')}
            cancelLabel={t('bulkTag.cancel')}
            busyLabel={t('bulkTag.acceptAiBusy')}
            busy={acceptAiBusy}
            onConfirm={() => {
              void confirmAcceptAiTags();
            }}
            onClose={closeAcceptAiTags}
          >
            <p>
              {interpolate(t('bulkTag.acceptAiBody'), { count: acceptAiTarget.length }, locale)}
            </p>
            {acceptAiError && (
              <p role="alert" className="flex items-start gap-2 text-danger">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{acceptAiError}</span>
              </p>
            )}
          </ConfirmModal>
        </Portal>
      )}

      {/* Confirmation « mettre à la poubelle » (corbeille RÉVERSIBLE, Lot 1) — portée
          hors de l'en-tête `backdrop-blur`. Le libellé insiste sur la réversibilité. */}
      {deleteTarget !== null && (
        <Portal>
          <ConfirmModal
            title={t('trash.confirmTitle')}
            confirmLabel={t('trash.confirmDelete')}
            cancelLabel={t('trash.cancel')}
            busyLabel={t('trash.deleting')}
            busy={deleteBusy}
            danger
            onConfirm={() => {
              void confirmDeleteSelection();
            }}
            onClose={closeDeleteSelection}
          >
            <p>{interpolate(t('trash.confirmBody'), { count: deleteTarget.length }, locale)}</p>
            {deleteError && (
              <p role="alert" className="flex items-start gap-2 text-danger">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{deleteError}</span>
              </p>
            )}
          </ConfirmModal>
        </Portal>
      )}

      {/* Confirmation « Tagger avec l'IA » (NOTRE modale, jamais `window.confirm`) —
          portée hors de l'en-tête `backdrop-blur`. Le message indique la portée
          (sélection ou toute la bibliothèque) ; les propositions se valident ensuite. */}
      {aiTagConfirm !== null && (
        <Portal>
          <ConfirmModal
            title={t('tools.tagAiConfirmTitle')}
            confirmLabel={t('tools.tagAiConfirmAction')}
            cancelLabel={t('tools.tagAiCancel')}
            busyLabel={t('tools.tagAiQueuing')}
            busy={aiTagBusy}
            onConfirm={() => {
              void confirmTagAi();
            }}
            onClose={cancelTagAi}
          >
            <p>{aiTagConfirm.message}</p>
          </ConfirmModal>
        </Portal>
      )}
    </GalleryViewerContext.Provider>
  );
}

/**
 * Vue combinée de l'onglet Dossiers (navigation façon EXPLORATEUR de fichiers).
 *
 * À CHAQUE niveau : (1) un fil d'Ariane de dossiers cliquable pour remonter ; (2) les
 * CARTES de sous-dossiers DIRECTS (`FolderGrid`, alimenté par les facettes scopées au
 * `parent` courant, bucket racine `''` exclu) ; (3) les photos DIRECTEMENT dans le
 * dossier courant (grille d'images, `folderDirect`). Descendre = cliquer une carte.
 *
 * Ce composant est REMONTÉ par chemin (clé `folders:<path>` côté parent) : chaque niveau
 * repart d'un défilement propre, et `useScrollRestore` (clé par chemin) restaure la
 * position au retour à un niveau visité. La visionneuse (`/photo/:id`) garde le même
 * chemin (état de retour) → pas de remontage, la grille de photos persiste (scroll intact).
 */
function FoldersTabView({
  libraryId,
  folderPath,
  folders,
  density,
  assets,
  selected,
  selectionMode,
  onToggleSelect,
  onSelectRange,
  onOpenAsset,
  onFolderAction,
}: {
  libraryId: string;
  folderPath: string;
  folders: ReturnType<typeof useFolderTree>;
  density: Density;
  assets: ReturnType<typeof useAssets>;
  selected: ReadonlySet<string>;
  /** Mode sélection actif — l'onglet Dossiers rend la MÊME grille, il le relaie tel quel. */
  selectionMode: boolean;
  onToggleSelect: (id: string) => void;
  onSelectRange: (ids: string[]) => void;
  onOpenAsset: (index: number) => void;
  /** Écritures de dossier (lot 4) — absent ⇒ lecture seule, aucune affordance rendue. */
  onFolderAction?: ((kind: FolderOpKind, path: string) => void) | undefined;
}): React.JSX.Element {
  const { t } = useI18n();
  // Filtre CLIENT des cartes de sous-dossiers par nom — LOCAL et indépendant de la
  // recherche photo `q` de la barre d'outils (réinitialisé par niveau via le remontage).
  const [cardSearch, setCardSearch] = useState('');

  // Sous-dossiers DIRECTS du dossier courant. On EXCLUT le bucket racine `''` : à la
  // racine, les facettes renvoient un `''` pour les fichiers directement à la racine —
  // ce ne sont PAS un sous-dossier (ils s'affichent en photos directes ci-dessous).
  const folderCards = useMemo(
    () => (folders.data ?? []).filter((f) => f.path !== ''),
    [folders.data],
  );
  const hasFolderCards = folderCards.length > 0;
  // Bloc « Sous-dossiers » rendu s'il y a des cartes OU une erreur de chargement à montrer.
  const showFolderCards = hasFolderCards || folders.errorCode !== null;

  // Réserve/restaure le défilement des cartes au retour à CE niveau (descendre puis
  // remonter). Clé PAR CHEMIN → chaque niveau restaure la sienne.
  const scrollKey = `${FOLDER_GRID_SCROLL_KEY}:${folderPath}`;
  const cardsScrollRef = useRef<HTMLDivElement>(null);
  const foldersReady = !folders.loading && folders.data !== null;
  useScrollRestore(scrollKey, cardsScrollRef, foldersReady, () => {
    /* Aucun supplément : seul le défilement des cartes est restauré. */
  });

  // Photos directes : on masque le bloc quand il n'y en a pas ET qu'il y a des cartes
  // (pas de « Aucun média » trompeur à côté de sous-dossiers). Sinon (chargement, erreur,
  // ou aucun sous-dossier) on rend `GalleryBody`, qui gère loading/erreur/vide/grille.
  const photosReadyEmpty = assets.status === 'ready' && assets.total === 0;
  const showPhotos = !(photosReadyEmpty && hasFolderCards);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      {/* Fil d'Ariane de dossiers (Dossiers / 2024 / vacances) — remonter d'un niveau,
          FLANQUÉ des actions qui portent sur le NIVEAU COURANT (XPHOTO-32 lot 4). */}
      <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <FolderPathNav folderPath={folderPath} />
        </div>
        {onFolderAction !== undefined && (
          <CurrentFolderActions folderPath={folderPath} onFolderAction={onFolderAction} />
        )}
      </div>

      {showFolderCards && (
        // Quand des photos DIRECTES cohabitent avec les sous-dossiers, on CAPE les cartes
        // (`max-h-80`) pour laisser de la place aux photos en dessous. Quand il n'y a QUE
        // des sous-dossiers (pas de photos directes, ex. à la racine), la section GRANDIT
        // pour remplir la page (`flex-1`) au lieu de rester coincée dans une petite boîte.
        <section
          aria-labelledby="folders-subfolders-heading"
          className={showPhotos ? 'shrink-0' : 'flex min-h-0 flex-1 flex-col'}
        >
          <h2
            id="folders-subfolders-heading"
            className="mb-2 shrink-0 text-sm font-semibold text-fg"
          >
            {t('folders.subfolders')}
          </h2>
          <div
            ref={cardsScrollRef}
            className={showPhotos ? 'max-h-80 overflow-y-auto' : 'min-h-0 flex-1 overflow-y-auto'}
          >
            <FolderGrid
              embedded
              folders={folderCards}
              loading={folders.loading}
              errorCode={folders.errorCode}
              density={density}
              search={cardSearch}
              onSearchChange={setCardSearch}
              onDrillIn={() => saveScrollStash(scrollKey, cardsScrollRef.current, null)}
              onFolderAction={onFolderAction}
            />
          </div>
        </section>
      )}

      {showPhotos && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {hasFolderCards && (
            <h2 className="mb-2 text-sm font-semibold text-fg">{t('folders.photosHere')}</h2>
          )}
          <GalleryBody
            libraryId={libraryId}
            assets={assets}
            density={density}
            selected={selected}
            selectionMode={selectionMode}
            onToggleSelect={onToggleSelect}
            onSelectRange={onSelectRange}
            onOpenAsset={onOpenAsset}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Actions portant sur le NIVEAU COURANT de l'onglet Dossiers (XPHOTO-32, lots 4 et 5) :
 * un bouton « Nouveau dossier » (le geste fréquent, donc direct) et un menu « ⋯ » pour
 * les gestes qui portent sur le dossier ouvert lui-même — renommer, déplacer, supprimer.
 *
 * À LA RACINE, le menu « ⋯ » N'EST PAS RENDU : la racine ne se renomme, ne se déplace ni
 * ne se supprime (c'est `libraries.root_path`, pas un dossier de la bibliothèque). Le
 * serveur le refuse de toute façon — mais offrir un geste pour le refuser ensuite est un
 * défaut d'UI, pas une défense. « Nouveau dossier », lui, garde tout son sens à la racine.
 *
 * Ces contrôles ne sont montés que si l'appelant a passé `onFolderAction`, c'est-à-dire
 * si l'utilisateur a l'accès écriture — masqués, pas désactivés.
 */
function CurrentFolderActions({
  folderPath,
  onFolderAction,
}: {
  folderPath: string;
  onFolderAction: (kind: FolderOpKind, path: string) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const atRoot = folderPath === '';
  const name = atRoot ? t('folders.root') : folderPath;
  return (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        onClick={() => onFolderAction('create', folderPath)}
        className={`inline-flex ${TOUCH_TARGET} items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-fg transition-colors hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none`}
      >
        <FolderPlusIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span>{t('folderOps.newFolder')}</span>
      </button>
      {!atRoot && (
        <ToolsMenu
          trigger={<EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />}
          label={interpolate(t('folderOps.menuLabel'), { name })}
          closeLabel={t('a11y.closeMenu')}
          actions={[
            {
              id: 'rename',
              label: t('folderOps.actionRename'),
              icon: <PencilSquareIcon className="h-5 w-5 shrink-0" aria-hidden="true" />,
              onSelect: () => onFolderAction('rename', folderPath),
            },
            {
              id: 'move',
              label: t('folderOps.actionMove'),
              icon: <FolderArrowDownIcon className="h-5 w-5 shrink-0" aria-hidden="true" />,
              onSelect: () => onFolderAction('move', folderPath),
            },
            // Détachée derrière un séparateur, en variante `danger` : le sens reste
            // porté par l'icône de corbeille et le libellé, jamais par la couleur seule.
            {
              id: 'delete',
              label: t('folderOps.actionDelete'),
              icon: <TrashIcon className="h-5 w-5 shrink-0" aria-hidden="true" />,
              onSelect: () => onFolderAction('delete', folderPath),
              separatorBefore: true,
              danger: true,
            },
          ]}
        />
      )}
    </div>
  );
}

/**
 * Fil d'Ariane de DOSSIERS (dans la vue Dossiers) : « Dossiers / 2024 / vacances ».
 * Chaque segment est un lien vers son dossier ancêtre (`?dossier=<chemin>`) ; « Dossiers »
 * mène à la racine. Le dernier segment (niveau courant) est un repère non cliquable
 * (`aria-current="page"`). À la racine, seul « Dossiers » (courant) est affiché.
 */
function FolderPathNav({ folderPath }: { folderPath: string }): React.JSX.Element {
  const { t } = useI18n();
  const segments = folderPathSegments(folderPath);
  const linkCls =
    'truncate rounded-md px-1.5 py-1 hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';
  const currentCls = 'truncate rounded-md px-1.5 py-1 font-medium text-fg';
  return (
    <nav aria-label={t('folders.breadcrumbLabel')} className="flex min-w-0 shrink-0 items-center">
      <ol className="flex min-w-0 flex-wrap items-center gap-1 text-sm text-muted">
        <li className="flex shrink-0 items-center">
          {segments.length === 0 ? (
            <span aria-current="page" className={currentCls}>
              {t('folders.title')}
            </span>
          ) : (
            <Link to="/dossiers" className={linkCls}>
              {t('folders.title')}
            </Link>
          )}
        </li>
        {segments.map((seg, index) => {
          const isLast = index === segments.length - 1;
          return (
            <Fragment key={seg.path}>
              <li aria-hidden="true" className="shrink-0 select-none text-muted">
                /
              </li>
              <li className="flex min-w-0 items-center">
                {isLast ? (
                  <span aria-current="page" className={currentCls}>
                    {seg.name}
                  </span>
                ) : (
                  <Link to={folderLinkFor(seg.path)} className={linkCls}>
                    {seg.name}
                  </Link>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

/** Auto-réessai 429 : nb max d'essais automatiques consécutifs avant repli manuel. */
const MAX_AUTO_RETRIES = 4;
/** Plancher du délai d'auto-réessai (évite une boucle serrée si `Retry-After` est minuscule). */
const MIN_RETRY_SECONDS = 2;

/** Corps de la galerie : arbitre entre pas-de-bibliothèque / erreur / vide / grille. */
function GalleryBody({
  libraryId,
  assets,
  density,
  selected,
  selectionMode,
  onToggleSelect,
  onSelectRange,
  onOpenAsset,
}: {
  libraryId: string;
  assets: ReturnType<typeof useAssets>;
  density: Density;
  selected: ReadonlySet<string>;
  /** Mode sélection actif — relayé tel quel à la grille (booléen, cf. `Thumbnail`). */
  selectionMode: boolean;
  onToggleSelect: (id: string) => void;
  onSelectRange: (ids: string[]) => void;
  onOpenAsset: (index: number) => void;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const { status, errorKind, retryAfter, reload } = assets;

  // Compte à rebours + AUTO-RÉESSAI sur 429 : un écran figé laisse croire à un plantage ;
  // ici le message « bouge » (secondes) et la galerie se recharge d'elle-même. Le délai
  // suit le `Retry-After` du serveur (planché à MIN_RETRY_SECONDS), avec un plafond
  // d'essais auto avant de rendre la main au bouton manuel (garde anti-boucle).
  // `GalleryBody` reste monté à travers le cycle error→loading→error : le compteur
  // d'essais (ref) survit d'un essai à l'autre.
  const [countdown, setCountdown] = useState<number | null>(null);
  const autoRetriesRef = useRef(0);

  // Une page chargée avec succès solde les auto-essais accumulés.
  useEffect(() => {
    if (status === 'ready') autoRetriesRef.current = 0;
  }, [status]);

  useEffect(() => {
    if (
      status !== 'error' ||
      errorKind !== 'rateLimited' ||
      autoRetriesRef.current >= MAX_AUTO_RETRIES
    ) {
      setCountdown(null);
      return;
    }
    let remaining = Math.max(MIN_RETRY_SECONDS, retryAfter ?? MIN_RETRY_SECONDS);
    setCountdown(remaining);
    const id = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(id);
        setCountdown(0);
        autoRetriesRef.current += 1;
        reload();
      } else {
        setCountdown(remaining);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [status, errorKind, retryAfter, reload]);

  if (!libraryId) {
    return (
      <EmptyState
        icon={<PhotoIcon className="h-10 w-10" aria-hidden="true" />}
        title={t('gallery.noLibrary')}
        hint={t('gallery.noLibraryHint')}
      />
    );
  }

  if (status === 'error') {
    // 429 (rate-limit) : message clair + compte à rebours d'auto-réessai (récupération seule).
    if (errorKind === 'rateLimited') {
      const autoActive = countdown !== null;
      return (
        <div className="flex flex-col items-center gap-3 py-16 text-center" role="status">
          <ExclamationTriangleIcon className="h-10 w-10 text-warning-text" aria-hidden="true" />
          <p className="font-medium text-fg">{t('gallery.errorRateLimitedTitle')}</p>
          {/* Compteur VISUEL (aria-hidden : évite l'annonce à chaque seconde). */}
          <p className="text-sm text-muted" aria-hidden="true">
            {autoActive
              ? interpolate(
                  t('gallery.errorRateLimitedRetryIn'),
                  {
                    seconds: String(Math.max(0, countdown ?? 0)),
                  },
                  locale,
                )
              : t('gallery.errorRateLimitedExhausted')}
          </p>
          {/* Libellé stable pour lecteurs d'écran (annoncé une fois). */}
          <span className="sr-only">
            {autoActive
              ? t('gallery.errorRateLimitedAuto')
              : t('gallery.errorRateLimitedExhausted')}
          </span>
          <button
            type="button"
            onClick={reload}
            className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {t('gallery.retryNow')}
          </button>
        </div>
      );
    }
    // Réseau / serveur (5xx) / autre : titre distinct + bouton manuel.
    const title =
      errorKind === 'network'
        ? t('gallery.errorNetworkTitle')
        : errorKind === 'server'
          ? t('gallery.errorServerTitle')
          : t('gallery.errorTitle');
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center" role="alert">
        <ExclamationTriangleIcon className="h-10 w-10 text-danger" aria-hidden="true" />
        <p className="text-fg">{title}</p>
        <button
          type="button"
          onClick={reload}
          className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          {t('gallery.retry')}
        </button>
      </div>
    );
  }

  if (status === 'loading' || status === 'idle') {
    return (
      <p className="py-16 text-center text-muted" role="status">
        {t('gallery.loading')}
      </p>
    );
  }

  if (assets.total === 0) {
    return (
      <EmptyState
        icon={<PhotoIcon className="h-10 w-10" aria-hidden="true" />}
        title={t('gallery.empty')}
        hint={t('gallery.emptyHint')}
      />
    );
  }

  return (
    <>
      {/* Échec d'une page AU-DELÀ de la première (audit F6) : bandeau NON BLOQUANT —
          la grille reste affichée et utilisable, seules les rangées manquantes sont
          concernées. Sans ce signal, elles restaient des skeletons pulsants à l'infini.
          Même vocabulaire d'erreur que la première page (nature discriminée). */}
      {assets.pageError !== null && (
        <div
          role="alert"
          className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm text-fg"
        >
          <ExclamationTriangleIcon
            className="h-5 w-5 shrink-0 text-warning-text"
            aria-hidden="true"
          />
          <span className="flex-1">
            {t(PAGE_ERROR_TITLE_KEY[assets.pageError.kind])}
            {assets.pageError.autoRetrying ? ` ${t('gallery.pageErrorAutoRetry')}` : ''}
          </span>
          <button
            type="button"
            onClick={assets.retryFailedPages}
            className="shrink-0 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {t('gallery.retry')}
          </button>
        </div>
      )}
      <Grid
        total={assets.total}
        loadedCount={assets.loadedCount}
        density={DENSITIES[density]}
        getAsset={assets.getAsset}
        ensureRange={assets.ensureRange}
        selected={selected}
        selectionMode={selectionMode}
        onToggleSelect={onToggleSelect}
        onSelectRange={onSelectRange}
        onOpenAsset={onOpenAsset}
      />
    </>
  );
}

/**
 * Titre du bandeau de page manquante, par NATURE d'erreur — réutilise le vocabulaire
 * déjà établi pour la première page (cohérence : un 429 se nomme pareil partout).
 */
const PAGE_ERROR_TITLE_KEY: Record<AssetsErrorKind, MessageKey> = {
  rateLimited: 'gallery.pageErrorRateLimited',
  network: 'gallery.pageErrorNetwork',
  server: 'gallery.pageErrorServer',
  unknown: 'gallery.pageErrorTitle',
};

function EmptyState({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2 py-16 text-center text-muted">
      {icon}
      <p className="font-medium text-fg">{title}</p>
      <p className="text-sm">{hint}</p>
    </div>
  );
}

/** Réexport de la liste d'onglets pour d'éventuels tests. */
export { GALLERY_TABS };
